import api from './api';

// ── Auth ──────────────────────────────────────────────────────
export const authAPI = {
  me: () => api.get('/auth/me'),
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (name, email, phone, password) => api.post('/auth/register', { name, email, phone, password }),
  logout: () => api.post('/auth/logout'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
};

// ── Users / Profile ───────────────────────────────────────────
export const usersAPI = {
  getProfile: () => api.get('/users/profile'),
  updateProfile: (data) => api.put('/users/profile', data),
  deleteProfile: (reason) => api.delete('/users/profile', { data: { reason } }),
  changePassword: (currentPassword, newPassword) =>
    api.put('/users/change-password', { currentPassword, newPassword }),
};

// ── Businesses ────────────────────────────────────────────────
export const businessAPI = {
  getAll: (params = {}) => api.get('/businesses', { params }),
  getOne: (id) => api.get(`/businesses/${id}`),
  create: (data) => api.post('/businesses', data),
  update: (id, data) => api.put(`/businesses/${id}`, data),
  remove: (id) => api.delete(`/businesses/${id}`),
  generateBusinessQrCode: (id) => api.get(`/businesses/${id}/qrcode`),
  generateQrCode: (text) => api.get('/businesses/qrcode', { params: { text } }),
};

// ── Business Progress ─────────────────────────────────────────
export const progressAPI = {
  getAll: (businessId) => api.get(`/businesses/${businessId}/progress`),
  create: (businessId, data) => api.post(`/businesses/${businessId}/progress`, data),
  update: (businessId, id, data) => api.put(`/businesses/${businessId}/progress/${id}`, data),
  remove: (businessId, id) => api.delete(`/businesses/${businessId}/progress/${id}`),
  uploadAttachment: (businessId, id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/businesses/${businessId}/progress/${id}/attachments`, formData);
  },
  downloadAttachment: (businessId, id, attachmentId) => api.get(
    `/businesses/${businessId}/progress/${id}/attachments/${attachmentId}/download`,
    { responseType: 'blob' }
  ),
  downloadCalendar: (businessId, id) => api.get(`/businesses/${businessId}/progress/${id}/calendar.ics`, { responseType: 'blob' }),
  removeAttachment: (businessId, id, attachmentId) => api.delete(`/businesses/${businessId}/progress/${id}/attachments/${attachmentId}`),
};

// ── Financial Entries ─────────────────────────────────────────
export const financialAPI = {
  getAll: (businessId) => api.get(`/businesses/${businessId}/financial`),
  create: (businessId, data) => api.post(`/businesses/${businessId}/financial`, data),
  update: (businessId, id, data) => api.put(`/businesses/${businessId}/financial/${id}`, data),
  remove: (businessId, id) => api.delete(`/businesses/${businessId}/financial/${id}`),
};

// ── Worked Hours ──────────────────────────────────────────────
export const workedHoursAPI = {
  getAll: (businessId) => api.get(`/businesses/${businessId}/worked-hours`),
  create: (businessId, data) => api.post(`/businesses/${businessId}/worked-hours`, data),
  update: (businessId, id, data) => api.put(`/businesses/${businessId}/worked-hours/${id}`, data),
  remove: (businessId, id) => api.delete(`/businesses/${businessId}/worked-hours/${id}`),
};

// ── Involved Persons ──────────────────────────────────────────
export const involvedAPI = {
  getAll: (businessId) => api.get(`/businesses/${businessId}/involved`),
  create: (businessId, data) => api.post(`/businesses/${businessId}/involved`, data),
  update: (businessId, id, data) => api.put(`/businesses/${businessId}/involved/${id}`, data),
  remove: (businessId, id) => api.delete(`/businesses/${businessId}/involved/${id}`),
};

// ── Clients ───────────────────────────────────────────────────
export const clientAPI = {
  getAll: (params = {}) => api.get('/clients', { params }),
  getOne: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  remove: (id) => api.delete(`/clients/${id}`),
};

export const uploadAPI = {
  image: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload', formData);
  },
};

// ── Reports ───────────────────────────────────────────────────
export const reportsAPI = {
  clients: (params = {}) => api.get('/reports/clients', { params }),
  businesses: (params = {}) => api.get('/reports/businesses', { params }),
  progress: (params = {}) => api.get('/reports/progress', { params: typeof params === 'string' ? { status: params } : params }),
  financial: (params = {}) => api.get('/reports/financial', { params }),
  workedHours: (params = {}) => api.get('/reports/worked-hours', { params }),
  involved: (params = {}) => api.get('/reports/involved', { params }),
  export: (kind, params = {}) => api.get(`/reports/${kind}/export`, { params, responseType: 'blob' }),
};

// ── Subscriptions ─────────────────────────────────────────────
export const subscriptionAPI = {
  getMy: () => api.get('/subscriptions/my'),
  getPlans: (region) => api.get('/subscriptions/plans', { params: { region } }),
  select: (data) => api.post('/subscriptions/select', data),
  checkoutSession: (data) => api.post('/subscriptions/checkout-session', data),
  completeCheckout: (data) => api.post('/subscriptions/checkout-session/complete', data),
  upgradePreview: () => api.get('/subscriptions/upgrade-preview'),
  upgradeCheckout: (data = {}) => api.post('/subscriptions/upgrade-checkout', data),
  upgradePaymentSheet: () => api.post('/subscriptions/upgrade-payment-sheet'),
  scheduleDowngrade: () => api.post('/subscriptions/schedule-downgrade'),
  cancelRenewal: () => api.post('/subscriptions/cancel-renewal'),
  customerPortal: (data = {}) => api.post('/subscriptions/customer-portal', data),
  paymentMethod: () => api.get('/subscriptions/payment-method'),
  paymentMethodSetup: () => api.post('/subscriptions/payment-method/setup-intent'),
  completePaymentMethodSetup: (data) => api.post('/subscriptions/payment-method/complete', data),
  paymentSheet: (data) => api.post('/subscriptions/payment-sheet', data),
  completePaymentSheet: (data) => api.post('/subscriptions/payment-sheet/complete', data),
  upgrade: () => api.post('/subscriptions/upgrade'),
  markPaid: () => api.post('/subscriptions/mark-paid'),
  cancel: () => api.post('/subscriptions/cancel'),
};

// ── Admin ─────────────────────────────────────────────────────
export const adminAPI = {
  getUsers: () => api.get('/admin/users'),
  updateUser: (id, data) => api.patch(`/admin/users/${id}`, data),
  updateSubscription: (id, data) => api.patch(`/admin/users/${id}/subscription`, data),
  syncAppleSubscription: (id) => api.post(`/admin/users/${id}/subscription/apple/sync`),
  deleteUser: (id, reason) => api.delete(`/admin/users/${id}`, { data: { reason } }),
  getDeletionLogs: () => api.get('/admin/deletion-logs'),
  blockUser: (id) => api.patch(`/admin/users/${id}/block`),
  unblockUser: (id) => api.patch(`/admin/users/${id}/unblock`),
};
